// =============================================================================
// components/voice/R2VoiceAssistant.tsx
// R2 Voice Engineering Assistant — Push-to-talk · Command Dispatch
// Commands: Open Project · Find Drawing · Generate Floor Plan · Create Telecom Room
// Show Permit Status · Start Clash Detection · Open Delivery Portal · Create Riser Diagram
// Future-ready: continuous conversation, TTS, multi-turn command sessions
// =============================================================================
import { useState, useRef, useEffect, useCallback, type JSX } from 'react';
import { Mic, MicOff, Loader2, Volume2, X, Radio, Cpu, Zap, Bot, ChevronRight, Globe, FileText, Building2, AlertTriangle, Package, Shield } from 'lucide-react';

const API_URL = (import.meta as any).env?.VITE_BACKEND_URL
  ?? (typeof process !== 'undefined' ? (process.env as any)?.REACT_APP_BACKEND_URL ?? '' : '');

const C = {
  bg:     'rgba(2,6,18,0.97)',
  panel:  'rgba(6,12,28,0.95)',
  border: 'rgba(30,50,90,0.4)',
  teal:   '#5eead4',
  amber:  '#fbbf24',
  blue:   '#60a5fa',
  green:  '#4ade80',
  red:    '#f87171',
  purple: '#a78bfa',
  text:   '#f0f4ff',
  muted:  'rgba(148,172,210,0.65)',
  dim:    'rgba(80,110,150,0.3)',
};

type RecordState = 'idle' | 'recording' | 'processing' | 'done' | 'error';

// Supported voice command examples with categories
const COMMAND_EXAMPLES = [
  { cmd: 'Open dashboard',           category: 'Navigation',   icon: <Globe size={11}/>,       color: C.teal   },
  { cmd: 'Show permit status',        category: 'Permits',      icon: <FileText size={11}/>,    color: C.blue   },
  { cmd: 'Open delivery portal',      category: 'Marketplace',  icon: <Package size={11}/>,     color: C.amber  },
  { cmd: 'Generate floor plan',       category: 'Drawing',      icon: <Building2 size={11}/>,   color: C.purple },
  { cmd: 'Create telecom room',       category: 'Drawing',      icon: <Cpu size={11}/>,         color: C.green  },
  { cmd: 'Create riser diagram',      category: 'Drawing',      icon: <Zap size={11}/>,         color: C.purple },
  { cmd: 'Start clash detection',     category: 'BIM',          icon: <AlertTriangle size={11}/>, color: C.red  },
  { cmd: 'Show mission control',      category: 'Navigation',   icon: <Radio size={11}/>,       color: C.teal   },
  { cmd: 'Find telecom drawing',      category: 'Search',       icon: <Shield size={11}/>,      color: C.blue   },
  { cmd: 'Open my orders',            category: 'Marketplace',  icon: <Package size={11}/>,     color: C.amber  },
];

interface R2VoiceAssistantProps {
  onNavigate?: (route: string) => void;
  onSearch?: (query: string) => void;
  onChatSend?: (message: string) => void;
  compact?: boolean;
}

function WaveformBars({ active }: { active: boolean }): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: 28 }}>
      {[0.4, 0.7, 1.0, 0.8, 0.5, 0.9, 0.6, 0.75, 0.45, 0.85].map((h, i) => (
        <div
          key={i}
          style={{
            width: 3, borderRadius: 2,
            background: C.teal,
            height: active ? `${h * 24}px` : '4px',
            opacity: active ? 0.8 : 0.25,
            transition: 'height 0.12s ease',
            animation: active ? `wave-bar ${0.4 + i * 0.06}s ease-in-out infinite alternate` : 'none',
          }}
        />
      ))}
      <style>{`
        @keyframes wave-bar {
          from { transform: scaleY(0.5); }
          to   { transform: scaleY(1.2); }
        }
      `}</style>
    </div>
  );
}

function ActionResult({ action, onNavigate, onSearch, onChatSend }: {
  action: any;
  onNavigate?: (r: string) => void;
  onSearch?:   (q: string) => void;
  onChatSend?: (m: string) => void;
}): JSX.Element {
  const [executed, setExecuted] = useState(false);

  const execute = () => {
    setExecuted(true);
    if (action.action === 'navigate' && onNavigate)      onNavigate(action.route);
    else if (action.action === 'search' && onSearch)     onSearch(action.query);
    else if (action.action === 'open_chat' && onChatSend) {
      window.dispatchEvent(new CustomEvent('r2:chat:send', { detail: { text: action.message } }));
      onChatSend(action.message);
    } else if (action.action === 'show_permits' && onNavigate)  onNavigate('r2-permit-intelligence');
    else if (action.action === 'show_orders' && onNavigate)     onNavigate('r2-delivery-portal');
  };

  const LABELS: Record<string, string> = {
    navigate:     `Navigate to ${action.route ?? ''}`,
    search:       `Search: "${action.query ?? ''}"`,
    open_chat:    `Ask R2: "${(action.message ?? '').slice(0, 50)}"`,
    show_permits: `Show permits (${action.filter ?? 'all'})`,
    show_orders:  'Open Delivery Portal',
    unknown:      'Command not recognized',
  };

  return (
    <div style={{ background: executed ? `${C.green}08` : `${C.teal}08`, border: `1px solid ${executed ? C.green : C.teal}25`, borderRadius: 10, padding: '10px 14px', marginTop: 10 }}>
      <div style={{ fontSize: 11, color: executed ? C.green : C.teal, marginBottom: 8, fontWeight: 600 }}>
        {executed ? 'Executed!' : 'Parsed Command'}
      </div>
      <div style={{ fontSize: 12, color: C.text, marginBottom: executed ? 0 : 10 }}>{LABELS[action.action] ?? action.action}</div>
      {!executed && action.action !== 'unknown' && (
        <button
          data-testid="voice-execute-btn"
          onClick={execute}
          style={{ background: `${C.teal}18`, border: `1px solid ${C.teal}35`, color: C.teal, borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}
        >
          <ChevronRight size={12}/> Execute
        </button>
      )}
    </div>
  );
}

export function R2VoiceAssistant({ onNavigate, onSearch, onChatSend, compact = false }: R2VoiceAssistantProps): JSX.Element {
  const [recordState, setRecordState] = useState<RecordState>('idle');
  const [transcript, setTranscript]   = useState('');
  const [action, setAction]           = useState<any | null>(null);
  const [errorMsg, setErrorMsg]       = useState('');
  const [showCommands, setShowCommands] = useState(!compact);
  const mediaRef  = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    setTranscript(''); setAction(null); setErrorMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setRecordState('processing');
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const form = new FormData(); form.append('audio', blob, 'voice.webm');
        try {
          const r = await fetch(`${API_URL}/api/r2-voice/transcribe-and-command`, { method: 'POST', body: form });
          const d = await r.json();
          setTranscript(d.transcript ?? '');
          setAction(d.command ?? null);
          setRecordState('done');
          if (d.command?.action !== 'unknown' && d.command) {
            window.dispatchEvent(new CustomEvent('r2:voice:command', { detail: { text: d.transcript ?? '', command: d.command } }));
          }
        } catch { setErrorMsg('Transcription failed. Please try again.'); setRecordState('error'); }
      };
      mr.start();
      mediaRef.current = mr;
      setRecordState('recording');
    } catch {
      setErrorMsg('Microphone access denied. Please enable it in browser settings.');
      setRecordState('error');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRef.current?.state === 'recording') {
      mediaRef.current.stop();
      mediaRef.current = null;
    }
  }, []);

  const reset = () => { setRecordState('idle'); setTranscript(''); setAction(null); setErrorMsg(''); };

  const handleTextCommand = async (text: string) => {
    setTranscript(text); setAction(null); setRecordState('processing');
    try {
      const r = await fetch(`${API_URL}/api/r2-voice/command`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const d = await r.json();
      setAction(d.command ?? null);
      setRecordState('done');
    } catch { setErrorMsg('Command parsing failed.'); setRecordState('error'); }
  };

  return (
    <div data-testid="r2-voice-assistant" style={{ background: C.bg, borderRadius: compact ? 0 : 12, padding: compact ? '12px 14px' : '18px 20px', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      {!compact && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `${C.teal}12`, border: `1px solid ${C.teal}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal }}>
            <Mic size={18}/>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>R2 Voice Assistant</div>
            <div style={{ fontSize: 10, color: C.muted }}>Speak or type a command · Hands-free engineering</div>
          </div>
        </div>
      )}

      {/* Main push-to-talk button */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button
          data-testid="voice-record-btn"
          onMouseDown={startRecording}
          onMouseUp={stopRecording}
          onTouchStart={e => { e.preventDefault(); void startRecording(); }}
          onTouchEnd={e => { e.preventDefault(); stopRecording(); }}
          disabled={recordState === 'processing'}
          style={{
            width: compact ? 52 : 72, height: compact ? 52 : 72, borderRadius: '50%',
            background: recordState === 'recording' ? `${C.red}20` : recordState === 'processing' ? `${C.amber}15` : `${C.teal}12`,
            border: `2px solid ${recordState === 'recording' ? C.red : recordState === 'processing' ? C.amber : C.teal}`,
            color: recordState === 'recording' ? C.red : recordState === 'processing' ? C.amber : C.teal,
            cursor: recordState === 'processing' ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
            boxShadow: recordState === 'recording' ? `0 0 24px ${C.red}40` : recordState === 'done' ? `0 0 16px ${C.teal}30` : 'none',
            animation: recordState === 'recording' ? 'voice-pulse 1.2s ease-in-out infinite' : 'none',
          }}
        >
          {recordState === 'processing' ? <Loader2 size={compact ? 20 : 28} style={{ animation: 'spin 1s linear infinite' }}/> :
           recordState === 'recording'  ? <MicOff size={compact ? 20 : 28}/> :
           <Mic size={compact ? 20 : 28}/>}
        </button>

        <WaveformBars active={recordState === 'recording'} />

        <div style={{ fontSize: 10, color: C.muted, textAlign: 'center' }}>
          {recordState === 'idle'       && 'Hold to speak · Release to process'}
          {recordState === 'recording'  && <span style={{ color: C.red }}>Listening…</span>}
          {recordState === 'processing' && <span style={{ color: C.amber }}>Processing command…</span>}
          {recordState === 'done'       && <span style={{ color: C.green }}>Command received</span>}
          {recordState === 'error'      && <span style={{ color: C.red }}>{errorMsg}</span>}
        </div>
      </div>

      {/* Transcript + Action */}
      {(transcript || action) && (
        <div style={{ marginBottom: 14 }}>
          {transcript && (
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 9, padding: '10px 12px', marginBottom: 6 }}>
              <div style={{ fontSize: 9, color: C.dim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>Transcription</div>
              <div style={{ fontSize: 12, color: C.text }}>"{transcript}"</div>
            </div>
          )}
          {action && (
            <ActionResult action={action} onNavigate={onNavigate} onSearch={onSearch} onChatSend={onChatSend}/>
          )}
          <button data-testid="voice-reset-btn" onClick={reset} style={{ marginTop: 8, background: 'none', border: 'none', color: C.dim, cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
            <X size={10}/> Clear
          </button>
        </div>
      )}

      {/* Quick text command */}
      {!compact && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 9, color: C.dim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 }}>Or type a command</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              data-testid="voice-text-input"
              placeholder="e.g. Open delivery portal…"
              onKeyDown={e => { if (e.key === 'Enter') { void handleTextCommand((e.target as HTMLInputElement).value); (e.target as HTMLInputElement).value = ''; } }}
              style={{ flex: 1, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 11, outline: 'none' }}
            />
          </div>
        </div>
      )}

      {/* Command examples */}
      {showCommands && (
        <div>
          <button
            onClick={() => setShowCommands(v => !v)}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em' }}
          >
            <Radio size={9}/> Example Commands
          </button>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {COMMAND_EXAMPLES.map(ex => (
              <button
                key={ex.cmd}
                data-testid={`voice-example-${ex.cmd.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => void handleTextCommand(ex.cmd)}
                style={{ background: `${ex.color}08`, border: `1px solid ${ex.color}20`, color: ex.color, borderRadius: 20, padding: '4px 10px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.12s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = `${ex.color}18`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = `${ex.color}08`; }}
              >
                {ex.icon} {ex.cmd}
              </button>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes voice-pulse{0%,100%{box-shadow:0 0 24px ${C.red}40}50%{box-shadow:0 0 40px ${C.red}70}}
      `}</style>
    </div>
  );
}
